import { styled, SxProps, Theme, Tooltip, TooltipProps } from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";

type TruncatedTextProps = {
  /** The full string — rendered as-is when no children are given, and used as the tooltip copy. */
  title: string;
  /**
   * Optional pre-styled content (a `<Text>`, a link…). Anything handed in is flattened to
   * `display: inline` inside the clamp: `<Text>` is a flex span, and a flex container clips its
   * text without ever drawing the "…".
   */
  children?: ReactNode;
  /**
   * Should carry an explicit px `maxWidth` (a responsive object is fine). Tables here lay out
   * `auto`, where a `max-width` on the cell is ignored — a long name just pushes the column
   * wider instead of ellipsising — so the clamp only holds if it sits in px on the text itself.
   */
  sx?: SxProps<Theme>;
  placement?: TooltipProps["placement"];
};

/**
 * One-line text with an ellipsis when it doesn't fit, and the full string on hover.
 * The tooltip is armed only when the text is actually clipped, so short names stay quiet.
 */
export const TruncatedText = ({ title, children, sx, placement = "top" }: TruncatedTextProps) => {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [clipped, setClipped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    let cancelled = false;
    // 1px of slack: sub-pixel text metrics leave scrollWidth a hair over clientWidth on strings
    // that do fit, which would arm a tooltip with nothing to reveal.
    const measure = () => {
      if (!cancelled) {
        setClipped(el.scrollWidth - el.clientWidth > 1);
      }
    };

    measure();
    // The clamp is a px width, so a breakpoint change resizes the box and the observer below
    // catches it. A late webfont swap doesn't — it widens the text inside a box that never
    // changes size — hence the extra pass once the fonts settle.
    document.fonts?.ready.then(measure).catch(() => undefined);

    if (typeof ResizeObserver === "undefined") {
      return () => {
        cancelled = true;
      };
    }

    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [title]);

  return (
    <Tooltip placement={placement} title={clipped ? title : ""}>
      <Clamp ref={ref} sx={sx}>
        {children ?? title}
      </Clamp>
    </Tooltip>
  );
};

const Clamp = styled("span")({
  display: "block",
  minWidth: 0,
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  // Doubled `&` on purpose: the children this wraps are styled components of their own, so the
  // flattening rule has to out-specify their own class rather than tie with it on insertion order.
  "&& > *": {
    display: "inline",
  },
});

export default TruncatedText;
